import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, BookOpen, Users, Star, CheckCircle, ArrowRight, ArrowLeft, Lock, Play, GraduationCap } from 'lucide-react';
import { getPathway } from '../utils/pathways';

const MOCK_COURSES = [
  { _id: '1', title: 'Computer Science Pathway', track: 'computer-science', level: 'beginner', price: 0, duration: 180, totalLessons: 52, enrollmentCount: 0, rating: 5.0, shortDescription: 'Master the fundamentals of computer science — algorithms, data structures, programming concepts and problem solving. Perfect starting point for any IT career.', sections: [{ title: 'Getting Started', lessons: [{ title: 'Introduction to Computer Science', duration: 1200, free: true }, { title: 'How Computers Work', duration: 1800, free: true }, { title: 'Binary & Data Representation', duration: 2100, free: false }] }, { title: 'Programming Fundamentals', lessons: [{ title: 'Variables & Data Types', duration: 1800, free: false }, { title: 'Control Flow & Loops', duration: 2400, free: false }, { title: 'Functions & Scope', duration: 2700, free: false }] }] },
  { _id: '2', title: 'Data Science Pathway', track: 'data-science', level: 'beginner', price: 0, duration: 160, totalLessons: 44, enrollmentCount: 0, rating: 5.0, shortDescription: 'Learn data analysis, statistics, Python, visualisation and machine learning basics. Turn raw data into powerful insights.', sections: [{ title: 'Data Foundations', lessons: [{ title: 'What is Data Science?', duration: 1200, free: true }, { title: 'Statistics Fundamentals', duration: 2000, free: false }] }] },
  { _id: '3', title: 'AI & Machine Learning Pathway', track: 'ai-ml', level: 'intermediate', price: 0, duration: 150, totalLessons: 40, enrollmentCount: 0, rating: 5.0, shortDescription: 'Explore artificial intelligence, neural networks, deep learning and real-world AI applications using Python and modern frameworks.', sections: [{ title: 'AI Fundamentals', lessons: [{ title: 'Introduction to AI', duration: 1500, free: true }, { title: 'Machine Learning Basics', duration: 2400, free: false }] }] },
  { _id: '4', title: 'Cybersecurity Pathway', track: 'cybersecurity', level: 'beginner', price: 0, duration: 140, totalLessons: 38, enrollmentCount: 0, rating: 5.0, shortDescription: 'Understand ethical hacking, network security, cryptography and cyber defence. Protect systems and launch a career in cybersecurity.', sections: [{ title: 'Security Basics', lessons: [{ title: 'Introduction to Cybersecurity', duration: 1500, free: true }, { title: 'Network Fundamentals', duration: 2000, free: false }] }] },
  { _id: '5', title: 'Full Stack Web Development', track: 'software-engineering', level: 'beginner', price: 0, duration: 200, totalLessons: 60, enrollmentCount: 0, rating: 5.0, shortDescription: 'Build complete web applications from scratch. HTML, CSS, JavaScript, React, Node.js, databases and deployment — all in one pathway.', sections: [{ title: 'Web Fundamentals', lessons: [{ title: 'HTML & CSS Basics', duration: 1800, free: true }, { title: 'JavaScript Essentials', duration: 2400, free: true }, { title: 'React Introduction', duration: 3000, free: false }] }] },
  { _id: '6', title: 'Mobile App Development', track: 'mobile-dev', level: 'beginner', price: 0, duration: 130, totalLessons: 36, enrollmentCount: 0, rating: 5.0, shortDescription: 'Create Android and iOS apps using React Native. From UI design to app store publishing — learn mobile development end to end.', sections: [{ title: 'React Native Basics', lessons: [{ title: 'Getting Started with React Native', duration: 1500, free: true }, { title: 'Building Your First App', duration: 2400, free: false }] }] },
  { _id: '7', title: 'Cloud & DevOps Pathway', track: 'cloud', level: 'intermediate', price: 0, duration: 120, totalLessons: 32, enrollmentCount: 0, rating: 5.0, shortDescription: 'Master AWS, Docker, Kubernetes, CI/CD pipelines and cloud architecture. Essential skills for modern software deployment.', sections: [{ title: 'Cloud Basics', lessons: [{ title: 'Introduction to Cloud Computing', duration: 1800, free: true }, { title: 'AWS Fundamentals', duration: 2700, free: false }] }] },
  { _id: '8', title: 'Game Development Pathway', track: 'game-dev', level: 'beginner', price: 0, duration: 140, totalLessons: 38, enrollmentCount: 0, rating: 5.0, shortDescription: 'Design and build 2D and 3D games using Unity and C#. Learn game mechanics, physics, animation and publishing.', sections: [{ title: 'Unity Basics', lessons: [{ title: 'Introduction to Game Development', duration: 1500, free: true }, { title: 'Unity Interface & Setup', duration: 1800, free: false }] }] },
];

const highlights = [
  'Self-paced video lessons',
  'Real-world projects & portfolio',
  'Certificate of completion',
  'Mentorship from AlteraCode developers',
  'Community support',
  'Lifetime access to content',
];

export default function CourseDetail() {
  const { id } = useParams();
  const course = MOCK_COURSES.find(c => c._id === id);

  if (!course) return (
    <div style={{ paddingTop: 68, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
      <h2 style={{ color: 'var(--ac-white)', fontSize: 24 }}>Course not found</h2>
      <Link to="/educenter" className="btn btn-outline"><ArrowLeft size={16} /> Back to Courses</Link>
    </div>
  );

  const pathway = getPathway(course.track);
  const tc = pathway.color;

  return (
    <div style={{ paddingTop: 68 }}>
      {/* Hero */}
      <section style={{
        padding: '4rem 0 3rem',
        background: `radial-gradient(ellipse at 30% 50%, ${tc}0f 0%, transparent 55%), var(--ac-navy)`,
        borderBottom: '1px solid var(--ac-border)',
      }}>
        <div className="container">
          <Link to="/educenter" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ac-muted)', textDecoration: 'none', marginBottom: '1.5rem' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--ac-cyan)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--ac-muted)'}
          >
            <ArrowLeft size={14} /> Back to Courses
          </Link>
          <div className="layout-hero-card">
            <div>
              <div className="badge badge-purple" style={{ marginBottom: '1rem' }}><GraduationCap size={14} /> Altera Scholar</div>
              <div className="pathway-tile" style={{
                width: 76, height: 76, marginBottom: '1rem',
                borderRadius: 'var(--radius-lg)',
                background: `linear-gradient(135deg, ${tc}20, ${tc}0a)`,
                border: `1px solid ${tc}30`,
              }}>
                <pathway.Icon size={38} color={tc} strokeWidth={1.5} />
              </div>
              <h1 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: 'var(--ac-white)', lineHeight: 1.2, marginBottom: '1rem' }}>
                {course.title}
              </h1>
              <p style={{ fontSize: 16, color: 'var(--ac-muted)', lineHeight: 1.8, maxWidth: 600, marginBottom: '1.5rem' }}>
                {course.shortDescription}
              </p>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: 'var(--ac-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Clock size={14} color={tc} /> {Math.round(course.duration / 60)}h total
                </span>
                <span style={{ fontSize: 13, color: 'var(--ac-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <BookOpen size={14} color={tc} /> {course.totalLessons} lessons
                </span>
                <span style={{ fontSize: 13, color: 'var(--ac-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Users size={14} color={tc} /> {course.enrollmentCount} enrolled
                </span>
                <span style={{ fontSize: 13, color: 'var(--ac-warn)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Star size={14} fill="var(--ac-warn)" /> {course.rating}
                </span>
                <span style={{ fontSize: 12, color: 'var(--ac-muted)', background: 'rgba(255,255,255,0.06)', padding: '3px 10px', borderRadius: 20, textTransform: 'capitalize' }}>
                  {course.level}
                </span>
              </div>
            </div>

            {/* Enrol Card */}
            <div style={{
              background: 'var(--ac-card)', border: '1px solid var(--ac-border2)',
              borderRadius: 'var(--radius-xl)', padding: '2rem', minWidth: 280,
              boxShadow: 'var(--shadow-card)',
            }}>
              <div style={{ fontSize: 34, fontWeight: 900, color: 'var(--ac-white)', marginBottom: 4 }}>
                {course.price === 0 ? 'Free' : `$${course.price}`}
              </div>
              <p style={{ fontSize: 12, color: 'var(--ac-muted)', marginBottom: '1.2rem' }}>
                Launching July 2026
              </p>

              {/* Enrolment status — registration is closed for all pathways */}
              <div role="status" style={{ marginBottom: '1rem' }}>
                <span className="status-closed" style={{ fontSize: 12.5, padding: '9px 14px' }}>
                  <span className="status-closed__dot" />
                  Enrollments are currently closed.
                </span>
              </div>
              <span className="btn btn-edu btn-disabled" aria-disabled="true" style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }}>
                Enrolment Closed
              </span>
              <Link to="/contact" className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }}>
                Notify Me <ArrowRight size={16} />
              </Link>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: '1.2rem' }}>
                {highlights.map((h, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ac-text)' }}>
                    <CheckCircle size={13} color="var(--ac-green)" /> {h}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="section">
        <div className="container" style={{ maxWidth: 760 }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--ac-white)', marginBottom: '2rem' }}>Course Curriculum</h2>
          {course.sections.map((section, si) => (
            <div key={si} style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ac-text)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Section {si + 1}: {section.title}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {section.lessons.map((lesson, li) => (
                  <div key={li} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 'var(--radius-md)',
                    background: 'var(--ac-card)', border: '1px solid var(--ac-border)',
                  }}>
                    {lesson.free
                      ? <Play size={15} color={tc} />
                      : <Lock size={15} color="var(--ac-muted)" />
                    }
                    <span style={{ flex: 1, fontSize: 14, color: lesson.free ? 'var(--ac-white)' : 'var(--ac-muted)' }}>
                      {lesson.title}
                    </span>
                    {lesson.free && <span style={{ fontSize: 11, color: tc, fontWeight: 600 }}>Preview</span>}
                    <span style={{ fontSize: 12, color: 'var(--ac-muted)' }}>{Math.floor(lesson.duration / 60)}m</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ textAlign: 'center', padding: '1.5rem', background: 'var(--ac-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--ac-border)', color: 'var(--ac-muted)', fontSize: 13 }}>
            + More lessons coming at launch · July 2026
          </div>
        </div>
      </section>
    </div>
  );
}
